import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'am' : 'pm'}`;

export const QueenReminderSettings = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/notification-settings`, { headers }).then(r => setSettings(r.data)).catch(() => setSettings({}));
  }, [token]);

  const update = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    try {
      await axios.put(`${API}/api/notification-settings`, { [key]: value }, { headers });
      toast.success('Reminder settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  const applyDeviceTz = async () => {
    const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      await axios.post(`${API}/api/users/me/timezone`, { timezone: deviceTz }, { headers });
      setSettings(prev => ({ ...prev, timezone: deviceTz }));
      toast.success(`Time zone set to ${deviceTz}`);
    } catch {
      toast.error('Failed to update time zone');
    }
  };

  if (!settings) return <div className="text-sm text-stone-400">Loading…</div>;
  const tz = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div data-testid="queen-reminder-settings" className="space-y-4 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
      <p className="text-sm font-semibold text-amber-900 flex items-center gap-2"><Crown className="h-4 w-4" /> Queen Rearing Reminders</p>
      <p className="text-xs text-stone-500 -mt-2">Capping, transfer and emergence (the day before and on the day), plus Queens Ready on Day 30</p>
      <div className="flex items-center justify-between">
        <Label className="font-medium">Email reminders</Label>
        <Switch data-testid="queen-reminders-email-switch" checked={settings.queen_reminders_email ?? true} onCheckedChange={(v) => update('queen_reminders_email', v)} />
      </div>
      <div className="flex items-center justify-between">
        <Label className="font-medium">In-app reminders</Label>
        <Switch data-testid="queen-reminders-inapp-switch" checked={settings.queen_reminders_inapp ?? true} onCheckedChange={(v) => update('queen_reminders_inapp', v)} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="font-medium">Send time</Label>
          <p className="text-xs text-stone-500">
            {tz}
            {tz !== Intl.DateTimeFormat().resolvedOptions().timeZone && (
              <button data-testid="queen-reminder-use-device-tz" type="button" onClick={() => applyDeviceTz()} className="ml-1 text-amber-700 underline">use this device's time zone</button>
            )}
          </p>
        </div>
        <Select value={String(settings.queen_reminder_hour ?? 6)} onValueChange={(v) => update('queen_reminder_hour', parseInt(v))}>
          <SelectTrigger data-testid="queen-reminder-hour-select" className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
